import type { TimezoneMode } from '../interfaces/IOption';
import type { PickerMode } from '../types/PickerMode';

/**
 * Утилиты для работы с датами и часовыми поясами
 */

/**
 * Создает дату с учетом настроек часового пояса
 * @param year Год
 * @param month Месяц (0-11)
 * @param day День
 * @param timezone Настройка часового пояса
 * @returns Дата с учетом часового пояса
 */
export function createDateWithTimezone(
  year: number,
  month: number,
  day: number,
  timezone: TimezoneMode = 'local',
): Date {
  if (timezone === 'utc') {
    return new Date(Date.UTC(year, month, day));
  }
  // Для 'local' или строковых часовых поясов используем локальное время
  // (строковые часовые пояса пока не поддерживаются)
  return new Date(year, month, day);
}

/**
 * Создает дату и время с учетом настроек часового пояса
 * @param year Год
 * @param month Месяц (0-11)
 * @param day День
 * @param hours Часы
 * @param minutes Минуты
 * @param timezone Настройка часового пояса
 * @returns Дата и время с учетом часового пояса
 */
export function createDateTimeWithTimezone(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  timezone: TimezoneMode = 'local',
): Date {
  if (timezone === 'utc') {
    return new Date(Date.UTC(year, month, day, hours, minutes));
  }
  // Для 'local' или строковых часовых поясов используем локальное время
  return new Date(year, month, day, hours, minutes);
}

/**
 * Получает компоненты даты с учетом часового пояса
 * @param date Дата
 * @param timezone Настройка часового пояса
 * @returns Объект с компонентами даты
 */
export function getDateComponentsWithTimezone(
  date: Date,
  timezone: TimezoneMode = 'local',
): {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
} {
  if (timezone === 'utc') {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
      hours: date.getUTCHours(),
      minutes: date.getUTCMinutes(),
    };
  }
  // Для 'local' или строковых часовых поясов используем локальное время
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
  };
}

/**
 * Получает день недели с учетом настроек часового пояса
 * @param date Дата
 * @param timezone Настройка часового пояса
 * @returns День недели (0-6)
 */
export function getWeekdayWithTimezone(date: Date, timezone: TimezoneMode = 'local'): number {
  if (timezone === 'utc') {
    return date.getUTCDay();
  }

  return date.getDay();
}

/**
 * Форматирует дату в строку для input элемента с учетом часового пояса
 * @param date Дата
 * @param inputType Тип input элемента
 * @param timezone Настройка часового пояса
 * @returns Отформатированная строка
 */
export function formatDateForInputWithTimezone(
  date: Date,
  inputType: string,
  timezone: TimezoneMode = 'local',
): string {
  const { year, month, day, hours, minutes } = getDateComponentsWithTimezone(date, timezone);

  const pad2 = (value: number): string => value.toString().padStart(2, '0');
  const monthStr = pad2(month + 1);
  const dayStr = pad2(day);
  const hoursStr = pad2(hours);
  const minutesStr = pad2(minutes);

  if (inputType === 'time') {
    return `${hoursStr}:${minutesStr}`;
  }

  if (inputType === 'month') {
    return `${year}-${monthStr}`;
  }

  if (inputType === 'datetime-local') {
    return `${year}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}`;
  }

  // По умолчанию: type="date"
  return `${year}-${monthStr}-${dayStr}`;
}

/**
 * Определяет тип значения для managed input.
 * Для кастомных text input используем mode, чтобы парсинг и форматирование
 * работали так же, как для соответствующих нативных input типов.
 */
export function resolveManagedInputType(inputType: string, mode: PickerMode): string {
  if (inputType !== 'text') {
    return inputType;
  }

  if (mode === 'time') {
    return 'time';
  }

  if (mode === 'datetime' || mode === 'datetime-local') {
    return 'datetime-local';
  }

  if (mode === 'month') {
    return 'month';
  }

  return 'date';
}

/**
 * Форматирует дату по displayFormat с поддержкой токенов yyyy, MM, dd, HH, mm.
 */
export function formatDateByPattern(
  date: Date,
  format: string,
  timezone: TimezoneMode = 'local',
): string {
  const { year, month, day, hours, minutes } = getDateComponentsWithTimezone(date, timezone);
  const pad2 = (value: number): string => value.toString().padStart(2, '0');

  const parts: Record<string, string> = {
    yyyy: year.toString(),
    MM: pad2(month + 1),
    dd: pad2(day),
    HH: pad2(hours),
    mm: pad2(minutes),
  };

  return format.replace(/yyyy|MM|dd|HH|mm/g, (token: string) => parts[token] ?? token);
}

/**
 * Форматирует вводимые цифры по displayFormat. Нужна для маски ручного ввода.
 */
export function formatDigitsByPattern(value: string, format: string): string {
  const tokenLengths: Record<string, number> = {
    yyyy: 4,
    MM: 2,
    dd: 2,
    HH: 2,
    mm: 2,
  };
  const tokenRegex = /yyyy|MM|dd|HH|mm/g;
  const digits = value.replace(/\D/g, '');
  const maxDigits = (format.match(tokenRegex) ?? []).reduce(
    (sum, token) => sum + tokenLengths[token],
    0,
  );
  const limitedDigits = digits.slice(0, maxDigits);

  if (limitedDigits === '') {
    return '';
  }

  let result = '';
  let digitIndex = 0;
  let formatIndex = 0;

  while (formatIndex < format.length && digitIndex < limitedDigits.length) {
    const token = format.slice(formatIndex).match(/^(yyyy|MM|dd|HH|mm)/)?.[0];

    if (token) {
      const length = tokenLengths[token];
      const nextDigits = limitedDigits.slice(digitIndex, digitIndex + length);
      result += nextDigits;
      digitIndex += nextDigits.length;
      formatIndex += token.length;
      continue;
    }

    if (result !== '' && digitIndex < limitedDigits.length) {
      result += format[formatIndex];
    }
    formatIndex += 1;
  }

  return result;
}

/**
 * Парсит строку по displayFormat с поддержкой токенов yyyy, MM, dd, HH, mm.
 */
export function parseDateByPattern(
  value: string,
  format: string,
  timezone: TimezoneMode = 'local',
  fallbackDate?: Date,
): Date | null {
  const tokenPatterns: Record<string, string> = {
    yyyy: '(\\d{4})',
    MM: '(\\d{2})',
    dd: '(\\d{2})',
    HH: '(\\d{2})',
    mm: '(\\d{2})',
  };
  const tokenRegex = /yyyy|MM|dd|HH|mm/g;
  const tokens = format.match(tokenRegex) ?? [];

  if (tokens.length === 0) {
    return null;
  }

  const escapedFormat = format.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = '^' + escapedFormat.replace(tokenRegex, token => tokenPatterns[token]) + '$';
  const match = value.match(new RegExp(pattern));
  if (!match) {
    return null;
  }

  const fallbackComponents = getDateComponentsWithTimezone(fallbackDate ?? new Date(), timezone);
  const values = new Map<string, number>();
  const hasDateToken = /yyyy|MM|dd/.test(format);

  tokens.forEach((token, index) => {
    values.set(token, Number(match[index + 1]));
  });

  const year = values.get('yyyy') ?? fallbackComponents.year;
  const month = (values.get('MM') ?? fallbackComponents.month + 1) - 1;
  const day = values.get('dd') ?? (hasDateToken ? 1 : fallbackComponents.day);
  const hours = values.get('HH') ?? 0;
  const minutes = values.get('mm') ?? 0;

  if (
    month < 0 ||
    month > 11 ||
    day < 1 ||
    day > 31 ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  const parsed =
    values.has('HH') || values.has('mm')
      ? createDateTimeWithTimezone(year, month, day, hours, minutes, timezone)
      : createDateWithTimezone(year, month, day, timezone);
  const parsedComponents = getDateComponentsWithTimezone(parsed, timezone);

  if (
    parsedComponents.year !== year ||
    parsedComponents.month !== month ||
    parsedComponents.day !== day ||
    (values.has('HH') && parsedComponents.hours !== hours) ||
    (values.has('mm') && parsedComponents.minutes !== minutes)
  ) {
    return null;
  }

  return parsed;
}

/**
 * Сравнивает даты по компонентам (год, месяц, день) с учетом часового пояса
 * @param date1 Первая дата
 * @param date2 Вторая дата
 * @param timezone Настройка часового пояса
 * @returns true если даты представляют один и тот же день
 */
export function isSameDayWithTimezone(
  date1: Date,
  date2: Date,
  timezone: TimezoneMode = 'local',
): boolean {
  const comp1 = getDateComponentsWithTimezone(date1, timezone);
  const comp2 = getDateComponentsWithTimezone(date2, timezone);

  return comp1.year === comp2.year && comp1.month === comp2.month && comp1.day === comp2.day;
}

/**
 * Получает timestamp для полночи указанной даты с учетом часового пояса
 * @param date Дата
 * @param timezone Настройка часового пояса
 * @returns Timestamp для полночи
 */
export function getMidnightTimestampWithTimezone(
  date: Date,
  timezone: TimezoneMode = 'local',
): number {
  const { year, month, day } = getDateComponentsWithTimezone(date, timezone);

  if (timezone === 'utc') {
    return Date.UTC(year, month, day);
  }
  // Для 'local' создаем новую дату в локальном времени
  return new Date(year, month, day).getTime();
}
